import { type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils.ts'

import { emptyVariants } from './empty.variants.ts'

/**
 * Frames the empty-state content and controls its background variant and vertical density.
 *
 * @summary Empty-state frame.
 */
function EmptyRoot(props: EmptyRoot.Props) {
  const { className, variant = 'base', size = 'compact', ...rest } = props
  return <div data-slot="empty" className={cn(emptyVariants({ variant, size, className }))} {...rest} />
}
namespace EmptyRoot {
  type Variants = NonNullable<VariantProps<typeof emptyVariants>>
  export type Props = React.ComponentProps<'div'> & {
    /** Variant de fond. @default 'base' */
    variant?: NonNullable<Variants['variant']> | undefined
    /** Densité verticale. @default 'compact' */
    size?: NonNullable<Variants['size']> | undefined
  }
}

function EmptyHeader(props: EmptyHeader.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="empty-header"
      className={cn('flex max-w-[400px] flex-col items-center gap-20', className)}
      {...rest}
    />
  )
}
namespace EmptyHeader {
  export type Props = React.ComponentProps<'div'>
}

function EmptyMedia(props: EmptyMedia.Props) {
  const { className, variant = 'default', ...rest } = props
  return (
    <div
      data-slot="empty-media"
      data-variant={variant}
      className={cn(
        'flex shrink-0 items-center justify-center text-content-base-basic',
        "data-[variant=icon]:[&_svg:not([class*='size-'])]:size-6",
        className,
      )}
      {...rest}
    />
  )
}
namespace EmptyMedia {
  export type Props = React.ComponentProps<'div'> & { variant?: 'default' | 'icon' }
}

function EmptyTitle(props: EmptyTitle.Props) {
  const { className, ...rest } = props
  return (
    <h2
      data-slot="empty-title"
      className={cn('m-0 font-heading text-60 leading-60 font-600 tracking-10 text-content-base-main', className)}
      {...rest}
    />
  )
}
namespace EmptyTitle {
  export type Props = React.ComponentProps<'h2'>
}

function EmptyDescription(props: EmptyDescription.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="empty-description"
      className={cn('text-20 leading-30 font-400 text-content-base-basic', className)}
      {...rest}
    />
  )
}
namespace EmptyDescription {
  export type Props = React.ComponentProps<'div'>
}

/**
 * Holds the actions. Below `md` they stack in a column at full width, in document order; from `md` up they sit on one
 * line at their own width, like `Dialog.Footer` and `ProductCard.Actions`.
 *
 * @summary Empty-state actions.
 */
function EmptyContent(props: EmptyContent.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="empty-content"
      className={cn(
        'flex w-full max-w-[400px] flex-wrap items-center justify-center gap-inline',
        'max-md:flex-col max-md:items-stretch',
        className,
      )}
      {...rest}
    />
  )
}
namespace EmptyContent {
  export type Props = React.ComponentProps<'div'>
}

/**
 * An empty state composed of media, a title, a description and optional actions.
 *
 * @summary Composable empty state.
 */
const Empty = {
  Root: EmptyRoot,
  Header: EmptyHeader,
  Media: EmptyMedia,
  Title: EmptyTitle,
  Description: EmptyDescription,
  Content: EmptyContent,
}

namespace Empty {
  export namespace Root {
    export type Props = EmptyRoot.Props
  }
  export namespace Header {
    export type Props = EmptyHeader.Props
  }
  export namespace Media {
    export type Props = EmptyMedia.Props
  }
  export namespace Title {
    export type Props = EmptyTitle.Props
  }
  export namespace Description {
    export type Props = EmptyDescription.Props
  }
  export namespace Content {
    export type Props = EmptyContent.Props
  }
}

export { Empty }
export { emptyVariants } from './empty.variants.ts'
